import { useLazyQuery, useQuery } from "@apollo/client";
import styled from "@emotion/styled";
import { IconDownload } from "@kompare/kompare-ui-library";
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DownloadService, i18n, mediaQuery, setOfferId, setProgressBarPercentage, useAppDispatch
} from 'src/common';
import { getFileByIdAdditional, GetTermsAndConditionsByIdQuery, GetTermsAndConditionsByIdQueryVariables, getTermsAndConditionsByOfferAdditionalQuery, GetTermsAndConditionsByOfferQuery, GetTermsAndConditionsByOfferQueryVariables } from "src/common/api/apollo/additional";
import { Card, Divider, Flex, Label, Loader, OfferHeaderCard, Text } from 'src/components';
import { colors } from 'src/theme';
import { AppRoutes } from '../../_router/app.routes';

const CardStyle2 = styled(Card)({
  padding: '16px 12px',
  [mediaQuery('mobile')]: {
    padding: '16px 20px',
  },
});

const TermsDocRowStyle = styled(Flex)({
  marginBottom: 16,
});

const TermsLinkStyle = styled.div({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  textDecoration: 'none',
  minWidth: 0,
});

const TermsLinkTextStyle = styled(Text)({
  marginLeft: 8,
  wordBreak: 'break-word',
  minWidth: 0,
});

const TermsAndConditionsPageAdditional = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams<{ offerId: string }>();
  const offerId = params.offerId as string;

  const [getFileById] = useLazyQuery<GetTermsAndConditionsByIdQuery, GetTermsAndConditionsByIdQueryVariables>(getFileByIdAdditional, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    dispatch(setProgressBarPercentage(0));
  }, [dispatch]);

  useEffect(() => {
    dispatch(setOfferId(offerId));
  }, [offerId, dispatch]);

  const { data: getTermsAndConditionsByOfferData, loading: termsAndConditionsByOfferLoading, error: termsAndConditionsByOfferError } = useQuery<GetTermsAndConditionsByOfferQuery, GetTermsAndConditionsByOfferQueryVariables>(getTermsAndConditionsByOfferAdditionalQuery, {
    variables: { id: offerId },
    skip: !offerId,
    fetchPolicy: 'network-only',
  });

  if (termsAndConditionsByOfferLoading) return <Loader page />;
  if (termsAndConditionsByOfferError) {
    navigate(AppRoutes.CUSTOMER.fullPath);
    return null;
  }
  const termsDocuments = getTermsAndConditionsByOfferData?.getTermsAndConditions?.documents ?? [];

  if (!getTermsAndConditionsByOfferData || !getTermsAndConditionsByOfferData.getTermsAndConditions || !getTermsAndConditionsByOfferData.getTermsOffer) return null;

  return (
    <>
      <Flex vertical gap={24}>
        <CardStyle2 radius={12}>
          <Flex vertical gap={16}>
            <OfferHeaderCard
              infoOnly
              logoUrl={getTermsAndConditionsByOfferData.getTermsAndConditions.logoUrl ?? undefined}
              offerLabel={getTermsAndConditionsByOfferData.getTermsOffer.product?.brand?.name ?? undefined}
              brandName={getTermsAndConditionsByOfferData.getTermsOffer.product?.name ?? undefined}
            />
            <Divider dashed />
            {termsDocuments.length > 0 ? (
              <>
                <Label type="medium" style={{ marginBottom: 8 }}>
                  {i18n.t('pages.termsAndConditionsPage.documentsLabel')}
                </Label>
                {termsDocuments.map(
                  (doc: any, idx: number) =>
                    doc?.fileId &&
                    doc?.fileName && (
                      <TermsDocRowStyle key={idx} align="center">
                        <TermsLinkStyle
                          role="button"
                          tabIndex={0}
                          onClick={() => getFileById({ variables: { fileId: doc.fileId } }).then((result) => {
                            const url = result.data?.getTermsAndConditionsById?.downloadUrl;
                            if (url) {
                              window.open(url, '_blank', 'noopener,noreferrer');
                            }
                          })}
                          onKeyDown={(e) =>
                            e.key === 'Enter' && DownloadService.downloadFile(doc.fileId, `${doc.fileName}`)
                          }
                        >
                          <IconDownload />
                          <TermsLinkTextStyle color={colors.text3} type="base" bold>
                            {doc.fileName}
                          </TermsLinkTextStyle>
                        </TermsLinkStyle>
                      </TermsDocRowStyle>
                    )
                )}
              </>
            ) : (
              <Text type="base" color={colors.text4}>
                {i18n.t('pages.termsAndConditionsPage.noDocuments')}
              </Text>
            )}
          </Flex>
        </CardStyle2>
      </Flex>
    </>
  );
};

export default TermsAndConditionsPageAdditional;
